import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

interface Lease {
	id: string;
	leaseCode?: string;
	tenant: {
		name: string;
		avatar?: string;
		email?: string;
	};
	property: {
		name: string;
		unit?: string;
	};
	startDate: string;
	endDate: string;
	status: string;
	daysToExpiry: number;
	rentAmount: number;
	securityDeposit?: number;
}

interface LeaseTimelineProps {
	leases: Lease[];
	onEditLease: (leaseId: string) => void;
	onRenewLease: (leaseId: string) => void;
	onDeleteLease?: (leaseId: string) => void;
	onLeaseClick?: (lease: Lease) => void;
}

const LeaseTimeline: React.FC<LeaseTimelineProps> = ({
	leases,
	onEditLease,
	onRenewLease,
	onDeleteLease,
	onLeaseClick,
}) => {
	const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [viewMode, setViewMode] = useState<"end" | "start">("end");

	const months = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];

	const getDaysInMonth = (month: number, year: number): number => {
		return new Date(year, month + 1, 0).getDate();
	};

	const getLeasesByDate = (date: Date): Lease[] => {
		return leases.filter((lease) => {
			const leaseDate =
				viewMode === "end"
					? new Date(lease.endDate)
					: new Date(lease.startDate);
			return (
				leaseDate.getDate() === date.getDate() &&
				leaseDate.getMonth() === date.getMonth() &&
				leaseDate.getFullYear() === date.getFullYear()
			);
		});
	};

	const getLeasesForSelectedDate = (): Lease[] => {
		if (!selectedDate) return [];
		return getLeasesByDate(selectedDate);
	};

	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string): string => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getStatusColor = (status: string, daysToExpiry: number): string => {
		if (status === "active") {
			if (daysToExpiry <= 30) return "bg-error/10 text-error border-error/20";
			if (daysToExpiry <= 60)
				return "bg-warning/10 text-warning border-warning/20";
			return "bg-success/10 text-success border-success/20";
		}
		if (status === "draft")
			return "bg-muted/10 text-muted-foreground border-border";
		if (status === "expired") return "bg-error/10 text-error border-error/20";
		if (status === "terminated")
			return "bg-muted/10 text-muted-foreground border-border";
		if (status === "renewed")
			return "bg-success/10 text-success border-success/20";
		return "bg-muted/10 text-muted-foreground border-border";
	};

	const renderCalendarGrid = () => {
		const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
		const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
		const days = [];

		// Empty cells for days before the first day of the month
		for (let i = 0; i < firstDayOfMonth; i++) {
			days.push(
				<div
					key={`empty-${i}`}
					className="h-24 border border-border bg-muted/20"
				></div>,
			);
		}

		// Days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			const currentDate = new Date(selectedYear, selectedMonth, day);
			const dayLeases = getLeasesByDate(currentDate);
			const isToday = currentDate.toDateString() === new Date().toDateString();

			days.push(
				<div
					key={day}
					className={`h-24 border border-border bg-card p-2 overflow-hidden cursor-pointer hover:bg-muted/30 smooth-transition ${
						isToday ? "ring-2 ring-primary" : ""
					} ${
						selectedDate &&
						currentDate.toDateString() === selectedDate.toDateString()
							? "bg-muted/50"
							: ""
					}`}
					onClick={() => {
						if (dayLeases.length > 0) {
							setSelectedDate(currentDate);
						}
					}}
				>
					<div
						className={`text-sm font-medium mb-1 ${
							isToday ? "text-primary" : "text-foreground"
						}`}
					>
						{day}
					</div>

					<div className="space-y-1">
						{dayLeases.slice(0, 2).map((lease: Lease) => (
							<div
								key={lease.id}
								className={`text-xs px-2 py-1 rounded truncate cursor-pointer border ${getStatusColor(
									lease.status,
									lease.daysToExpiry,
								)} hover:opacity-80 smooth-transition`}
								onClick={(e) => {
									e.stopPropagation();
									if (onLeaseClick) {
										onLeaseClick(lease);
									} else {
										onEditLease(lease.id);
									}
								}}
								title={`${lease.tenant.name} - ${
									lease.property.name
								} (${formatCurrency(lease.rentAmount)})`}
							>
								{lease.tenant.name}
							</div>
						))}
						{dayLeases.length > 2 && (
							<div
								className="text-xs text-muted-foreground px-2 cursor-pointer hover:text-foreground"
								onClick={(e) => {
									e.stopPropagation();
									setSelectedDate(currentDate);
								}}
							>
								+{dayLeases.length - 2} more
							</div>
						)}
					</div>
				</div>,
			);
		}

		return days;
	};

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			{/* Timeline Header */}
			<div className="p-6 border-b border-border">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<h3 className="text-lg font-semibold text-foreground">
						Lease Timeline View
					</h3>

					<div className="flex flex-wrap items-center gap-4">
						{/* View Mode Toggle */}
						<div className="flex items-center space-x-2 bg-muted/50 rounded-lg p-1">
							<button
								onClick={() => setViewMode("start")}
								className={`px-3 py-1.5 rounded text-sm font-medium smooth-transition ${
									viewMode === "start"
										? "bg-card text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								Start Dates
							</button>
							<button
								onClick={() => setViewMode("end")}
								className={`px-3 py-1.5 rounded text-sm font-medium smooth-transition ${
									viewMode === "end"
										? "bg-card text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								End Dates
							</button>
						</div>

						<div className="flex items-center space-x-4">
							<div className="flex items-center space-x-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										if (selectedMonth === 0) {
											setSelectedMonth(11);
											setSelectedYear(selectedYear - 1);
										} else {
											setSelectedMonth(selectedMonth - 1);
										}
									}}
									iconName="ChevronLeft"
								/>

								<div className="text-center min-w-[140px]">
									<span className="font-medium text-foreground">
										{months[selectedMonth]} {selectedYear}
									</span>
								</div>

								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										if (selectedMonth === 11) {
											setSelectedMonth(0);
											setSelectedYear(selectedYear + 1);
										} else {
											setSelectedMonth(selectedMonth + 1);
										}
									}}
									iconName="ChevronRight"
								/>
							</div>

							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setSelectedMonth(new Date().getMonth());
									setSelectedYear(new Date().getFullYear());
									setSelectedDate(null);
								}}
							>
								Today
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Legend */}
			<div className="p-4 border-b border-border bg-muted/20">
				<div className="flex flex-wrap items-center gap-4 text-sm">
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-success/10 border border-success/20 rounded"></div>
						<span className="text-foreground">Active (60+ days)</span>
					</div>
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-warning/10 border border-warning/20 rounded"></div>
						<span className="text-foreground">Expiring Soon (30-60 days)</span>
					</div>
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-error/10 border border-error/20 rounded"></div>
						<span className="text-foreground">Critical (&lt;30 days)</span>
					</div>
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-muted/10 border border-border rounded"></div>
						<span className="text-foreground">Draft/Expired/Terminated</span>
					</div>
					<div className="flex items-center space-x-2 ml-auto">
						<span className="text-muted-foreground">
							Showing:{" "}
							{viewMode === "start" ? "Lease Start Dates" : "Lease End Dates"}
						</span>
					</div>
				</div>
			</div>

			{/* Desktop Calendar View */}
			<div className="hidden lg:block">
				{leases.filter((lease) => {
					const leaseDate = new Date(
						viewMode === "end" ? lease.endDate : lease.startDate,
					);
					return (
						leaseDate.getMonth() === selectedMonth &&
						leaseDate.getFullYear() === selectedYear
					);
				}).length === 0 ? (
					<div className="p-12 text-center">
						<Icon
							name="Calendar"
							size={64}
							className="mx-auto text-muted-foreground mb-4 opacity-50"
						/>
						<p className="text-lg font-medium text-foreground mb-2">
							No leases found for {months[selectedMonth]} {selectedYear}
						</p>
						<p className="text-sm text-muted-foreground">
							Try switching to{" "}
							{viewMode === "end" ? "start dates" : "end dates"} view or select
							a different month
						</p>
					</div>
				) : (
					<>
						{/* Calendar Header */}
						<div className="grid grid-cols-7 border-b border-border">
							{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
								<div
									key={day}
									className="p-3 text-center font-medium text-muted-foreground bg-muted/50"
								>
									{day}
								</div>
							))}
						</div>

						{/* Calendar Grid */}
						<div className="grid grid-cols-7">{renderCalendarGrid()}</div>
					</>
				)}
			</div>

			{/* Selected Date Details Panel */}
			{selectedDate && getLeasesForSelectedDate().length > 0 && (
				<div className="p-6 border-t border-border bg-muted/20">
					<div className="flex items-center justify-between mb-4">
						<h4 className="font-semibold text-foreground">
							Leases for {formatDate(selectedDate.toISOString())}
						</h4>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setSelectedDate(null)}
							iconName="X"
						>
							Close
						</Button>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{getLeasesForSelectedDate().map((lease: Lease) => (
							<div
								key={lease.id}
								className="bg-card border border-border rounded-lg p-4 hover:shadow-md smooth-transition cursor-pointer"
								onClick={() => {
									if (onLeaseClick) {
										onLeaseClick(lease);
									} else {
										onEditLease(lease.id);
									}
								}}
							>
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center space-x-3">
										<Image
											src={lease.tenant.avatar || ""}
											alt={lease.tenant.name}
											className="w-10 h-10 rounded-full object-cover"
										/>
										<div>
											<p className="font-medium text-foreground">
												{lease.tenant.name}
											</p>
											<p className="text-sm text-muted-foreground">
												{lease.property.name}
											</p>
										</div>
									</div>
									<span
										className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
											lease.status,
											lease.daysToExpiry,
										)}`}
									>
										{lease.status}
									</span>
								</div>

								<div className="space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Lease Code:</span>
										<span className="text-foreground font-mono">
											{lease.leaseCode || "N/A"}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Rent:</span>
										<span className="text-foreground font-medium">
											{formatCurrency(lease.rentAmount)}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">
											{viewMode === "start" ? "Start Date:" : "End Date:"}
										</span>
										<span className="text-foreground">
											{formatDate(
												viewMode === "start" ? lease.startDate : lease.endDate,
											)}
										</span>
									</div>
									{lease.daysToExpiry !== undefined && (
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												Days to Expiry:
											</span>
											<span
												className={`font-medium ${
													lease.daysToExpiry <= 30
														? "text-error"
														: lease.daysToExpiry <= 60
														? "text-warning"
														: "text-foreground"
												}`}
											>
												{lease.daysToExpiry > 0
													? `${lease.daysToExpiry} days`
													: "Expired"}
											</span>
										</div>
									)}
								</div>

								<div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
									<Button
										variant="outline"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											onEditLease(lease.id);
										}}
										iconName="Edit"
										iconPosition="left"
										className="flex-1"
									>
										Edit
									</Button>
									{lease.status === "active" && (
										<Button
											variant="outline"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												onRenewLease(lease.id);
											}}
											iconName="RefreshCw"
											iconPosition="left"
											className="flex-1"
										>
											Renew
										</Button>
									)}
									{onDeleteLease && (
										<Button
											variant="outline"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												onDeleteLease(lease.id);
											}}
											iconName="Trash2"
											className="text-destructive hover:text-destructive"
										>
											<Icon name="Trash2" size={16} />
										</Button>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Mobile/Tablet List View */}
			<div className="lg:hidden">
				<div className="p-4">
					{leases.filter((lease) => {
						const leaseDate = new Date(
							viewMode === "end" ? lease.endDate : lease.startDate,
						);
						return (
							leaseDate.getMonth() === selectedMonth &&
							leaseDate.getFullYear() === selectedYear
						);
					}).length === 0 ? (
						<div className="text-center py-12">
							<Icon
								name="Calendar"
								size={48}
								className="mx-auto text-muted-foreground mb-4 opacity-50"
							/>
							<p className="text-muted-foreground">
								No leases found for {months[selectedMonth]} {selectedYear}
							</p>
							<p className="text-sm text-muted-foreground mt-2">
								Try switching to{" "}
								{viewMode === "end" ? "start dates" : "end dates"} view
							</p>
						</div>
					) : (
						<div className="space-y-4">
							{leases
								.filter((lease) => {
									const leaseDate = new Date(
										viewMode === "end" ? lease.endDate : lease.startDate,
									);
									return (
										leaseDate.getMonth() === selectedMonth &&
										leaseDate.getFullYear() === selectedYear
									);
								})
								.sort(
									(a: Lease, b: Lease) =>
										new Date(a.endDate).getTime() -
										new Date(b.endDate).getTime(),
								)
								.map((lease: Lease) => (
									<div
										key={lease.id}
										className="border border-border rounded-lg p-4 hover:bg-muted/30 smooth-transition cursor-pointer"
										onClick={() => {
											if (onLeaseClick) {
												onLeaseClick(lease);
											} else {
												onEditLease(lease.id);
											}
										}}
									>
										<div className="flex items-center justify-between mb-3">
											<div className="flex items-center space-x-3">
												<Image
													src={lease.tenant.avatar || ""}
													alt={lease.tenant.name}
													className="w-10 h-10 rounded-full object-cover"
												/>
												<div>
													<p className="font-medium text-foreground">
														{lease.tenant.name}
													</p>
													<p className="text-sm text-muted-foreground">
														{lease.property.name}
													</p>
												</div>
											</div>

											<div
												className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
													lease.status,
													lease.daysToExpiry,
												)}`}
											>
												{lease.daysToExpiry > 0
													? `${lease.daysToExpiry} days`
													: "Expired"}
											</div>
										</div>

										<div className="grid grid-cols-2 gap-4 mb-4">
											<div>
												<p className="text-xs text-muted-foreground">
													End Date
												</p>
												<p className="font-medium text-foreground">
													{new Date(lease.endDate).toLocaleDateString("en-US", {
														month: "short",
														day: "numeric",
														year: "numeric",
													}) ?? "N/A"}
												</p>
											</div>
											<div>
												<p className="text-xs text-muted-foreground">
													Rent Amount
												</p>
												<p className="font-medium text-foreground">
													{formatCurrency(lease.rentAmount)}
												</p>
											</div>
										</div>

										<div
											className="flex items-center space-x-2"
											onClick={(e) => e.stopPropagation()}
										>
											<Button
												variant="outline"
												size="sm"
												onClick={() => onEditLease(lease.id)}
												iconName="Edit"
												iconPosition="left"
											>
												Edit
											</Button>
											{lease.status === "active" && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => onRenewLease(lease.id)}
													iconName="RefreshCw"
													iconPosition="left"
												>
													Renew
												</Button>
											)}
											{onDeleteLease && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => onDeleteLease(lease.id)}
													className="text-destructive hover:text-destructive"
												>
													<Icon name="Trash2" size={16} />
												</Button>
											)}
										</div>
									</div>
								))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default LeaseTimeline;
